import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import AdminPageShell, { AdminSurface } from "../../../components/Admin/AdminPageShell";
import { getApiUrl, getMediaUrl } from "../../../utils/site";

const EditProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState({
    name: "",
    category: "",
    price: "",
    stock_quantity: "",
    image_url: "",
    description: "",
    material: "",
    weight: "",
    size: "",
    status: "active",
  });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(getApiUrl(`/api/products/${id}`), {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setProduct({
          name: response.data.name || "",
          category: response.data.category || "",
          price: response.data.price || "",
          stock_quantity: response.data.stock_quantity || "",
          image_url: response.data.image_url || "",
          description: response.data.description || "",
          material: response.data.material || "",
          weight: response.data.weight || "",
          size: response.data.size || "",
          status: response.data.status || "active",
        });
      } catch (error) {
        toast.error(`Failed to fetch product data: ${error.response?.data?.error || error.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setProduct((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setUpdating(true);

    try {
      await axios.put(
        getApiUrl(`/api/products/${id}`),
        {
          ...product,
          price: Number(product.price),
          stock_quantity: Number(product.stock_quantity),
          weight: product.weight ? Number(product.weight) : null,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      toast.success("Jewellery updated successfully.");
      navigate("/admin/products");
    } catch (error) {
      toast.error(`Failed to update jewellery: ${error.response?.data?.error || error.message}`);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="roha-admin-empty">
        <Spinner color="info" /> Loading jewellery...
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Edit Product | Roha Jewellery Admin</title>
        <meta name="description" content="Edit a luxury gemstone or gold jewellery product in the Roha Jewellery store." />
      </Helmet>
      <AdminPageShell
        eyebrow="Catalogue control"
        title="Edit Product"
        description="Update product details, stock, pricing, media, and storefront visibility in the same polished admin workflow."
      >
        <AdminSurface title="Product details" subtitle={`Editing product #${id}`}>
          <form className="roha-admin-form-grid" onSubmit={handleSubmit}>
            <div className="roha-admin-form-grid roha-admin-form-grid--two">
              <div className="roha-admin-field">
                <label>Jewellery name</label>
                <input className="form-control" name="name" value={product.name} onChange={handleChange} required />
              </div>
              <div className="roha-admin-field">
                <label>Category</label>
                <input className="form-control" name="category" value={product.category} onChange={handleChange} required />
              </div>
            </div>

            <div className="roha-admin-form-grid roha-admin-form-grid--two">
              <div className="roha-admin-field">
                <label>Price (ETB)</label>
                <input className="form-control" type="number" name="price" min="0" step="0.01" value={product.price} onChange={handleChange} required />
              </div>
              <div className="roha-admin-field">
                <label>Stock quantity</label>
                <input className="form-control" type="number" name="stock_quantity" min="0" value={product.stock_quantity} onChange={handleChange} required />
              </div>
            </div>

            <div className="roha-admin-form-grid roha-admin-form-grid--two">
              <div className="roha-admin-field">
                <label>Material</label>
                <input className="form-control" name="material" value={product.material} onChange={handleChange} />
              </div>
              <div className="roha-admin-field">
                <label>Weight (g)</label>
                <input className="form-control" type="number" name="weight" min="0" step="0.01" value={product.weight} onChange={handleChange} />
              </div>
            </div>

            <div className="roha-admin-form-grid roha-admin-form-grid--two">
              <div className="roha-admin-field">
                <label>Size</label>
                <input className="form-control" name="size" value={product.size} onChange={handleChange} />
              </div>
              <div className="roha-admin-field">
                <label>Status</label>
                <select className="form-select" name="status" value={product.status} onChange={handleChange}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="roha-admin-field">
              <label>Image URL</label>
              <input className="form-control" name="image_url" value={product.image_url} onChange={handleChange} />
            </div>

            <div className="roha-admin-field">
              <label>Image preview</label>
              <img
                src={getMediaUrl(product.image_url)}
                alt={product.name || "Jewellery preview"}
                className="roha-admin-image-thumb"
                style={{ width: 120, height: 120 }}
                onError={(event) => {
                  event.currentTarget.onerror = null;
                  event.currentTarget.src = "/image.jpg";
                }}
              />
            </div>

            <div className="roha-admin-field">
              <label>Description</label>
              <textarea className="form-control" name="description" rows="5" value={product.description} onChange={handleChange} />
            </div>

            <div className="roha-admin-toolbar">
              <button type="submit" className="roha-admin-navbar__chip roha-admin-navbar__chip--primary" disabled={updating}>
                {updating ? "Updating..." : "Update jewellery"}
              </button>
              <button type="button" className="roha-admin-navbar__chip" onClick={() => navigate(-1)}>
                Cancel
              </button>
            </div>
          </form>
        </AdminSurface>
      </AdminPageShell>
    </>
  );
};

export default EditProduct;
